import { Component } from '@theme/component';
import { formatPriceCode } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents, ProductSelectEvent } from '@shopify/events';

// Joins variant option values in data-values / data-main-options; values can contain a single '|'
// ("Heather Grey | Green Patch"), so a double bar keeps them whole
const OPTION_SEPARATOR = '||';

/**
 * Saving badge text, mirror of snippets/ssc-saving-label.liquid: the percentage when that number is
 * bigger than the amount in whole currency units, else the amount ("SAVE 11%" / "SAVE EUR 30").
 * @param {number} saving - Saving in cents
 * @param {number} regular - Regular total in cents
 * @param {{ percent: string, amount: string }} labels - Labels with [percent] / [saving] tokens
 * @param {(cents: number) => string} format
 */
function savingLabel(saving, regular, labels, format) {
  if (saving <= 0 || regular <= 0) return '';
  const percent = Math.round((saving * 100) / regular);
  if (percent > saving / 100) return labels.percent.replace('[percent]', String(percent));
  return labels.amount.replace('[saving]', format(saving));
}

/**
 * "Complete the set" (blocks/ssc-complete-the-set.liquid).
 * - Follows the main product's variant (product select event): main variant ID, price and availability.
 * - Partner selects follow the main size until the shopper picks a partner chip themselves.
 * - Chip availability depends on the rows above: a chip is crossed out when no available variant has
 *   its value together with the values checked in the earlier rows (colour first, then size).
 *   Picking a colour whose checked size is sold out unchecks the size (no jump to another size): the
 *   hidden select falls back to its blank option, "Pick a size" shows and the button is disabled.
 * - The partner image follows the chosen variant's own image, else the product image.
 * - With a set saving, the first partner's price reads "<s>EUR 125</s> EUR 95" for the chosen variant.
 * - Recomputes "SET: EUR 200 | INSTEAD OF EUR 230" (or "TOGETHER: EUR 230" without a saving) and the
 *   saving badge in the heading row ("SAVE EUR 30" or "SAVE 13%", see savingLabel()).
 * - Gender alternatives (rows with the same data-group, e.g. men's and women's half tight) are an
 *   either/or: the MEN'S | WOMEN'S switch shows one row and hides the other; hidden rows count
 *   neither in the total nor in the set.
 * - "Add set to cart" posts all lines in one /cart/add.js call and dispatches the theme's
 *   CartLinesUpdateEvent the same way assets/product-form.js does, so the cart drawer and the
 *   header count refresh, then opens the cart drawer.
 *
 * @typedef {{ total: HTMLElement, button: HTMLButtonElement, hint: HTMLElement, status: HTMLElement, badge?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class SscCompleteTheSet extends Component {
  requiredRefs = ['total', 'button', 'status'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #adding = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
      signal: this.#abortController.signal,
    });

    // Start with the partner in the same size as the selected main variant
    const mainOptions = (this.dataset.mainOptions ?? '').split(OPTION_SEPARATOR).filter(Boolean);
    if (mainOptions.length) this.#matchPartnerSizes(mainOptions);
    for (const select of this.#partnerSelects) this.#refreshItem(select);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * Fields of the partners in the set (not of a hidden gender alternative).
   * @returns {Array<HTMLSelectElement | HTMLInputElement>}
   */
  get #partnerFields() {
    return /** @type {Array<HTMLSelectElement | HTMLInputElement>} */ (
      Array.from(this.querySelectorAll('[data-partner-variant]')).filter((field) => this.#inSet(field))
    );
  }

  /** @param {Element} field */
  #inSet(field) {
    const item = field.closest('[data-partner]');
    return !(item instanceof HTMLElement && item.hidden);
  }

  /** @returns {HTMLSelectElement[]} */
  get #partnerSelects() {
    return Array.from(this.querySelectorAll('select[data-partner-variant]')).filter(
      (field) => field instanceof HTMLSelectElement
    );
  }

  /** A partner has a row without a checked chip. */
  get #incomplete() {
    return this.#partnerSelects.some((select) => this.#inSet(select) && !select.value);
  }

  /**
   * "EUR 125" / "EUR 54.90" (snippets/price-code.liquid).
   * @param {number} cents
   */
  #format(cents) {
    return formatPriceCode(cents, this.dataset.currency ?? '');
  }

  /**
   * Shows the partner's price for the chosen variant; with a set saving on this partner as
   * "<s>EUR 125</s> EUR 95" with screen reader labels (same markup as the Liquid block).
   * @param {Element} item
   * @param {number} cents
   */
  #renderPartnerPrice(item, cents) {
    const priceElement = item.querySelector('[data-partner-price]');
    if (!(priceElement instanceof HTMLElement)) return;
    const savings = Number(priceElement.dataset.savings) || 0;

    if (savings <= 0) {
      priceElement.textContent = this.#format(cents);
      return;
    }

    /** @param {string} text */
    const hiddenLabel = (text) => {
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${text} `;
      return label;
    };
    const regular = document.createElement('s');
    regular.className = 'ssc-complete-set__price-regular';
    regular.textContent = this.#format(cents);
    const setPrice = document.createElement('span');
    setPrice.className = 'ssc-complete-set__price-set';
    setPrice.textContent = this.#format(Math.max(cents - savings, 0));
    priceElement.replaceChildren(
      hiddenLabel(this.dataset.labelRegular ?? ''),
      regular,
      hiddenLabel(this.dataset.labelPartnerSet ?? ''),
      setPrice
    );
  }

  /** @param {HTMLSelectElement | HTMLInputElement} field */
  #fieldPrice(field) {
    if (field instanceof HTMLSelectElement) {
      // Incomplete selection: keep the partner's last price in the total
      return Number(field.selectedOptions[0]?.dataset.price ?? field.dataset.lastPrice) || 0;
    }
    return Number(field.dataset.price) || 0;
  }

  #render() {
    const partnersTotal = this.#partnerFields.reduce((sum, field) => sum + this.#fieldPrice(field), 0);
    const combined = (Number(this.dataset.mainPrice) || 0) + partnersTotal;
    const savings = Number(this.dataset.savings) || 0;
    const { total, badge } = this.refs;

    if (badge) {
      badge.textContent = savingLabel(
        savings,
        combined,
        { percent: this.dataset.labelSavePercent ?? '', amount: this.dataset.labelSave ?? '' },
        (cents) => this.#format(cents)
      );
    }

    total.replaceChildren();
    if (savings > 0) {
      const strong = document.createElement('strong');
      strong.textContent = (this.dataset.labelSet ?? '').replace('[set]', this.#format(Math.max(combined - savings, 0)));
      total.append(strong, ' | ', (this.dataset.labelInstead ?? '').replace('[total]', this.#format(combined)));
    } else {
      total.textContent = (this.dataset.labelTogether ?? '').replace('[total]', this.#format(combined));
    }

    const available = this.dataset.mainAvailable === 'true';
    this.refs.button.disabled = !available || this.#adding || this.#incomplete;
    if (this.refs.hint) this.refs.hint.hidden = available;
  }

  /**
   * The option rows (fieldsets) of a partner, in option order.
   * @param {Element} item
   * @returns {HTMLFieldSetElement[]}
   */
  #rows(item) {
    return Array.from(item.querySelectorAll('fieldset[data-option-index]'))
      .filter((fieldset) => fieldset instanceof HTMLFieldSetElement)
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
  }

  /**
   * @param {HTMLFieldSetElement} row
   * @returns {HTMLInputElement[]}
   */
  #chips(row) {
    return Array.from(row.querySelectorAll('input[data-chip]')).filter((chip) => chip instanceof HTMLInputElement);
  }

  /**
   * Disables (crosses out) the chips without an available variant for the rows checked above them,
   * row by row; a checked chip that becomes unavailable is unchecked.
   * @param {HTMLSelectElement} select
   */
  #updateAvailability(select) {
    const item = select.closest('[data-partner]');
    if (!item) return;
    // Every option of the hidden select is an available variant (the blank one excepted)
    const combos = Array.from(select.options)
      .filter((option) => option.value)
      .map((option) => (option.dataset.values ?? '').split(OPTION_SEPARATOR));

    /** @type {string[]} */
    const checked = [];
    this.#rows(item).forEach((row, index) => {
      for (const chip of this.#chips(row)) {
        const available = combos.some(
          (combo) => combo[index] === chip.value && checked.every((value, prior) => !value || combo[prior] === value)
        );
        chip.disabled = !available;
        if (!available && chip.checked) chip.checked = false;
      }
      checked.push(this.#chips(row).find((chip) => chip.checked)?.value ?? '');
    });
  }

  /**
   * Selects the variant of the checked chips in the hidden select (the blank option while a row has
   * no checked chip), then updates the price, the "pick a size" hint and the image.
   * @param {HTMLSelectElement} select
   */
  #applySelection(select) {
    const item = select.closest('[data-partner]');
    if (!item) return;
    const wanted = this.#rows(item).map((row) => this.#chips(row).find((chip) => chip.checked)?.value ?? '');
    const complete = wanted.every(Boolean);
    const match = complete
      ? Array.from(select.options).find((option) => option.value && option.dataset.values === wanted.join(OPTION_SEPARATOR))
      : undefined;

    if (match) {
      match.selected = true;
      if (match.dataset.price) {
        select.dataset.lastPrice = match.dataset.price;
        this.#renderPartnerPrice(item, Number(match.dataset.price) || 0);
      }

    } else {
      select.value = '';
    }

    const hint = item.querySelector('[data-pick-hint]');
    if (hint) hint.textContent = match ? '' : this.dataset.labelPick ?? '';
    this.#updateImage(item, match);
  }

  /**
   * Shows the variant's own image when it has one, else the product image.
   * @param {Element} item
   * @param {HTMLOptionElement | undefined} option
   */
  #updateImage(item, option) {
    const image = item.querySelector('img.ssc-complete-set__image');
    if (!(image instanceof HTMLImageElement)) return;
    const src = option?.dataset.image || image.dataset.fallbackSrc;
    const srcset = option?.dataset.image ? option.dataset.imageSrcset : image.dataset.fallbackSrcset;
    // No colour image and no product image to fall back to: leave the current image
    if (!src || image.getAttribute('src') === src) return;
    image.setAttribute('src', src);
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
  }

  /**
   * Availability, then the selection that follows from the checked chips.
   * @param {HTMLSelectElement} select
   */
  #refreshItem(select) {
    this.#updateAvailability(select);
    this.#applySelection(select);
  }

  /** @param {Event} event */
  handlePartnerChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    select.dataset.touched = 'true';
    this.#syncChips(select);
    this.#setStatus('');
    this.#render();
  }

  /**
   * A colour or size chip of a partner was picked: recompute which chips are available for it and
   * select the matching variant. A size that is sold out in the new colour is unchecked, not replaced.
   * @param {Event} event
   */
  handleChipChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const select = input.closest('[data-partner]')?.querySelector('select[data-partner-variant]');
    if (!(select instanceof HTMLSelectElement)) return;

    select.dataset.touched = 'true';
    this.#refreshItem(select);
    this.#setStatus('');
    this.#render();
  }

  /**
   * Checks the chips that belong to the select's current variant, then refreshes availability.
   * @param {HTMLSelectElement} select
   */
  #syncChips(select) {
    const item = select.closest('[data-partner]');
    if (!item) return;
    const values = (select.selectedOptions[0]?.dataset.values ?? '').split(OPTION_SEPARATOR);
    this.#rows(item).forEach((row, index) => {
      for (const chip of this.#chips(row)) chip.checked = Boolean(values[index]) && chip.value === values[index];
    });
    this.#refreshItem(select);
  }

  /**
   * Preselects the partner variant whose option values include the most of the main variant's values
   * (same size), keeping the partner's current colour on a tie, unless the shopper already picked one.
   * @param {string[]} mainOptions
   */
  #matchPartnerSizes(mainOptions) {
    const wanted = mainOptions.map((value) => String(value).toLowerCase());
    for (const select of this.#partnerSelects) {
      if (select.dataset.touched === 'true') continue;
      const current = (select.selectedOptions[0]?.dataset.values ?? '').split(OPTION_SEPARATOR);
      let match = null;
      let best = 0;
      for (const option of select.options) {
        if (!option.value) continue;
        const values = (option.dataset.values ?? '').split(OPTION_SEPARATOR);
        // Shared values with the main variant count most, the current first option (colour) breaks ties
        const shared = values.filter((value) => wanted.includes(value)).length;
        if (!shared) continue;
        const score = shared * 2 + (values[0] === current[0] ? 1 : 0);
        if (score > best) {
          best = score;
          match = option;
        }
      }
      if (match && !match.selected) {
        match.selected = true;
        this.#syncChips(select);
      }
    }
  }

  /**
   * MEN'S | WOMEN'S switch: shows the chosen row of the group, hides the others, keeps focus on
   * the switch and recomputes the total.
   * @param {MouseEvent} event
   */
  handleGroupSwitch(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-switch-to]') : null;
    if (!(button instanceof HTMLElement)) return;
    const item = button.closest('[data-partner]');
    const target = button.dataset.switchTo;
    if (!(item instanceof HTMLElement) || !item.dataset.group || !target || item.dataset.partnerId === target) return;

    const rows = Array.from(this.querySelectorAll('[data-partner][data-group]')).filter(
      (row) => row instanceof HTMLElement && row.dataset.group === item.dataset.group
    );
    for (const row of rows) {
      if (row instanceof HTMLElement) row.hidden = row.dataset.partnerId !== target;
    }
    const shown = rows.find((row) => row instanceof HTMLElement && row.dataset.partnerId === target);
    const pressed = shown?.querySelector(`[data-switch-to="${target}"]`);
    if (pressed instanceof HTMLElement) pressed.focus();
    this.#setStatus('');
    this.#render();
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected) return;

        const { newProduct, productId, resource } = detail;
        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && String(productId) !== this.dataset.productId) {
          return;
        }

        this.dataset.mainVariantId = resource?.id ? String(resource.id) : '';
        this.dataset.mainPrice = String(resource?.price ?? 0);
        this.dataset.mainAvailable = String(Boolean(resource?.available));
        if (Array.isArray(resource?.options)) this.#matchPartnerSizes(resource.options);
        this.#setStatus('');
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ssc-complete-the-set] Event promise rejected:', error);
      });
  };

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    const { status } = this.refs;
    status.textContent = message;
    status.classList.toggle('ssc-complete-set__status--error', isError);
  }

  /** @param {boolean} on */
  #setAdding(on) {
    this.#adding = on;
    const { button } = this.refs;
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    this.#render();
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Opens the cart drawer (snippets/cart-drawer.liquid, <theme-drawer id="cart-drawer">) unless the
   * theme's auto-open (Theme settings > Cart > auto-open) already did. No-op when the cart is a page.
   */
  #openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || drawer.hasAttribute('open') || !('open' in drawer) || typeof drawer.open !== 'function') return;
    drawer.open();
  }

  async addSet() {
    if (this.#adding) return;
    const mainVariantId = this.dataset.mainVariantId;
    if (!mainVariantId || this.dataset.mainAvailable !== 'true' || this.#incomplete) return;

    const ids = [mainVariantId, ...this.#partnerFields.map((field) => field.value).filter(Boolean)];
    const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    this.#setStatus('');
    this.#setAdding(true);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: id, quantity: 1 })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await this.#fetchCart().catch(() => null);
        if (cart) {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'ssc-complete-the-set', itemCount: 0 },
          });
        } else {
          deferredEventPromise.reject(new Error('Add set failed'));
        }
        this.#setStatus(data.description || this.dataset.labelError || '', true);
        return;
      }

      const cart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ssc-complete-the-set',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      // After the cart listeners registered on the event promise have run
      deferredEventPromise.promise.then(() => this.#openCartDrawer()).catch(() => {});
      this.#setStatus(this.dataset.labelAdded ?? '');
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setStatus(this.dataset.labelError ?? '', true);
    } finally {
      this.#setAdding(false);
    }
  }
}

if (!customElements.get('ssc-complete-the-set')) {
  customElements.define('ssc-complete-the-set', SscCompleteTheSet);
}
